<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue'

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string
      language?: string
      size?: 'normal' | 'compact' | 'flexible'
      action?: string
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ) => string
  remove: (widgetId: string) => void
  reset: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

const props = defineProps<{
  sitekey: string
  locale: 'de' | 'en'
}>()

const emit = defineEmits<{
  token: [token: string]
  expired: []
  error: []
}>()

const widgetEl = ref<HTMLDivElement>()
let widgetId: string | undefined
let scriptPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    if (window.turnstile) return resolve()
    const existing = document.querySelector(
      'script[src*="challenges.cloudflare.com/turnstile"]',
    ) as HTMLScriptElement | null
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Turnstile script load failed')))
      return
    }
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Turnstile script load failed'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

async function renderWidget() {
  if (!widgetEl.value) return
  try {
    await loadScript()
    if (!window.turnstile || !widgetEl.value) return
    widgetId = window.turnstile.render(widgetEl.value, {
      sitekey: props.sitekey,
      language: props.locale,
      size: 'flexible',
      action: 'partner-form',
      callback: (token) => emit('token', token),
      'expired-callback': () => emit('expired'),
      'error-callback': () => emit('error'),
    })
  } catch {
    // Cloudflare blocked or offline. Fail open: emit empty token so submit still works.
    // Backend layer (honeypot + timer + per-IP rate limit) remains active.
    emit('token', '')
  }
}

onMounted(renderWidget)

watch(
  () => props.locale,
  () => {
    if (widgetId && window.turnstile) {
      window.turnstile.remove(widgetId)
      widgetId = undefined
      renderWidget()
    }
  },
)

onBeforeUnmount(() => {
  if (widgetId && window.turnstile) {
    window.turnstile.remove(widgetId)
  }
})
</script>

<template>
  <div ref="widgetEl" class="turnstile" />
</template>

<style scoped>
.turnstile {
  margin: var(--space-4) 0;
  min-height: 65px;
}
</style>
